import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// styles
import * as styles from "./DoctorCarousel.module.css";

// icons
import leftArrow from "../../../images/icons/left_arrw.png";
import rightArrow from "../../../images/icons/right_arrw.png";

// components
import DoctorCard from "./DoctorCard";

const DoctorCarousel = ({
  doctors,
  onDoctorClick,
  maxDoctors = 6,
  autoPlay = true,
  autoPlayInterval = 3000,
  showDots = false,
  showArrows = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const [isMobile, setIsMobile] = useState(false);
  const carouselRef = useRef(null);
  const autoPlayRef = useRef(null);

  // Limit doctors to maxDoctors
  const displayDoctors = doctors.slice(0, maxDoctors);
  const totalSlides = displayDoctors.length;
  const maxIndex = Math.max(0, totalSlides - slidesToShow);

  // Handle responsive slides
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 767) {
        setSlidesToShow(1);
        setIsMobile(true);
      } else if (width <= 991) {
        setSlidesToShow(2);
        setIsMobile(false);
      } else if (width <= 1200) {
        setSlidesToShow(3);
        setIsMobile(false);
      } else {
        setSlidesToShow(4);
        setIsMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto play functionality
  useEffect(() => {
    if (autoPlay) {
      autoPlayRef.current = setInterval(() => {
        setIsTransitioning(true);
        setCurrentIndex(prev => {
          // Loop back to start when reaching the end
          if (prev >= maxIndex) {
            return 0;
          }
          return prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 600);
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, maxIndex]);

  const handlePrevious = () => {
    if (isTransitioning || currentIndex === 0) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(0, prev - 1));
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const handleNext = () => {
    if (isTransitioning || currentIndex >= maxIndex) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const handleDotClick = index => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  // Calculate transform
  const slideWidth = 100 / slidesToShow;
  const gap = 24;
  const gapOffset = isMobile ? 0 : currentIndex * gap;
  const transform = isMobile
    ? "none"
    : `translateX(calc(-${currentIndex * slideWidth}% - ${gapOffset}px))`;

  // Generate dots based on visible groups
  const totalDots = maxIndex + 1;

  // For mobile, only show current item
  const displayItems = isMobile
    ? [displayDoctors[currentIndex]]
    : displayDoctors;

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.carouselContainer} ref={carouselRef}>
        <div
          className={styles.carouselTrack}
          style={{
            transform,
            transition:
              isTransitioning && !isMobile
                ? "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
                : "none",
          }}
        >
          {displayItems.map((doctor, index) => {
            const actualIndex = isMobile ? currentIndex : index;
            return (
              <div
                key={doctor.id}
                className={styles.carouselSlide}
                style={{
                  flex: isMobile
                    ? "0 0 100%"
                    : `0 0 calc(${slideWidth}% - ${gap}px)`,
                  marginRight:
                    !isMobile && index < displayDoctors.length - 1
                      ? `${gap}px`
                      : "0",
                }}
              >
                <DoctorCard
                  doctor={doctor}
                  onClick={() => onDoctorClick(doctor)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className={styles.carouselControls}>
        {/* Dots */}
        {showDots && totalDots > 1 && (
          <div className={styles.carouselDots}>
            {Array.from({ length: totalDots }).map((_, index) => (
              <button
                key={index}
                className={`${styles.dot} ${
                  index === currentIndex ? styles.dotActive : ""
                }`}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Arrow Buttons */}
        {showArrows && totalSlides > slidesToShow && (
          <div className={styles.carouselArrows}>
            <button
              className={`${styles.arrowButton} ${styles.arrowPrev} ${
                currentIndex === 0 ? styles.arrowDisabled : ""
              }`}
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              aria-label="Previous slide"
            >
              <img src={leftArrow} alt="Previous" />
            </button>
            <button
              className={`${styles.arrowButton} ${styles.arrowNext} ${
                currentIndex === maxIndex ? styles.arrowDisabled : ""
              }`}
              onClick={handleNext}
              disabled={currentIndex === maxIndex}
              aria-label="Next slide"
            >
              <img src={rightArrow} alt="Next" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

DoctorCarousel.propTypes = {
  doctors: PropTypes.array.isRequired,
  onDoctorClick: PropTypes.func.isRequired,
  maxDoctors: PropTypes.number,
  autoPlay: PropTypes.bool,
  autoPlayInterval: PropTypes.number,
  showDots: PropTypes.bool,
  showArrows: PropTypes.bool,
};

export default DoctorCarousel;
